import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { CheckCircle, Clock, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "checking" });
  const polled = useRef(0);
  const nav = useNavigate();
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) {
      setStatus({ state: "error", message: "No session found." });
      return;
    }
    const poll = async () => {
      if (polled.current >= 10) {
        setStatus({ state: "timeout" });
        return;
      }
      polled.current++;
      try {
        const { data } = await http.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus({ state: "paid", data });
          refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus({ state: "expired" });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setStatus({ state: "error", message: "Unable to verify payment." });
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="pt-32 pb-24 min-h-[80vh] flex items-center justify-center" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="glass-panel rounded-lg p-12 max-w-lg w-full text-center"
      >
        {status.state === "checking" && (
          <>
            <Clock className="w-12 h-12 mx-auto text-pink-hot mb-6 animate-pulse" />
            <h2 className="font-display text-4xl text-champagne mb-3">Verifying payment...</h2>
            <p className="text-sm text-champagne/70">One moment, darling.</p>
          </>
        )}
        {status.state === "paid" && (
          <>
            <CheckCircle className="w-14 h-14 mx-auto text-pink-hot mb-6 glow-pink rounded-full" />
            <h2 className="font-display text-4xl text-champagne mb-3">
              Thank <span className="italic text-pink-hot">you</span>
            </h2>
            <p className="text-sm text-champagne/80 mb-6">
              {status.data.type === "booking" && "Your deposit is received. Expect a confirmation message shortly."}
              {status.data.type === "subscription" && "Welcome to the Diamond Circle. Enjoy your private access."}
              {status.data.type === "unlock" && "Content unlocked. Enjoy, gorgeous."}
            </p>
            <Link
              to={status.data.type === "subscription" || status.data.type === "unlock" ? "/gallery" : "/account"}
              className="inline-block px-8 py-3 bg-pink-hot text-white text-xs tracking-[0.3em] uppercase rounded-sm hover:bg-pink-glow transition-all"
              data-testid="post-payment-continue"
            >
              Continue
            </Link>
          </>
        )}
        {status.state === "expired" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-rosegold mb-6" />
            <h2 className="font-display text-3xl text-champagne mb-3">Session expired</h2>
            <p className="text-sm text-champagne/70 mb-5">Please try again.</p>
            <button onClick={() => nav(-1)} className="text-pink-hot text-xs tracking-[0.28em] uppercase pink-underline-link">Go back</button>
          </>
        )}
        {status.state === "timeout" && (
          <>
            <Clock className="w-12 h-12 mx-auto text-rosegold mb-6" />
            <h2 className="font-display text-3xl text-champagne mb-3">Still processing</h2>
            <p className="text-sm text-champagne/70">You'll receive an email when confirmed.</p>
          </>
        )}
        {status.state === "error" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-red-400 mb-6" />
            <h2 className="font-display text-3xl text-champagne mb-3">Something went wrong</h2>
            <p className="text-sm text-champagne/70">{status.message}</p>
          </>
        )}
      </motion.div>
    </div>
  );
}
